/* eslint-disable */
// Session 55.ad · Inventory Health dashboard.
// One-shot audit of every data-integrity check that powers the /spaces
// search filters. Read-only report + one-click Self-Heal button that runs
// the fix-states, resync-workspace-locations, and backfill-card-photos
// chain in a single POST.
import { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import api from "../lib/api";

export default function AdminInventoryHealth() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [healing, setHealing] = useState(false);
  const [sending, setSending] = useState(false);
  const [lastHealResult, setLastHealResult] = useState(null);
  const [digestMsg, setDigestMsg] = useState("");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const r = await api.get("/admin/audit/inventory-health");
      setReport(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Load failed.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const runSelfHeal = async () => {
    if (!confirm("Run self-heal? Repairs missing states, resyncs workspace locations from venues, and backfills missing card photos across every listing. Idempotent — safe to run any time.")) return;
    setHealing(true); setErr("");
    try {
      const r = await api.post("/admin/audit/inventory-health/self-heal");
      setLastHealResult(r.data);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Self-heal failed.");
    } finally {
      setHealing(false);
    }
  };

  const sendDigest = async () => {
    if (!confirm("Run the nightly job now and send the digest email to every admin? Rate-limited to once per 23h.")) return;
    setSending(true); setErr(""); setDigestMsg("");
    try {
      await api.post("/admin/audit/inventory-health/run-nightly");
      setDigestMsg("✓ Digest sent (check admin inboxes).");
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Digest send failed.");
    } finally {
      setSending(false);
    }
  };

  return (
    <AppShell>
      <div className="m-page-pad" data-testid="admin-inventory-health">
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-end", gap:20, flexWrap:"wrap"}}>
          <div>
            <span className="m-eyebrow">Admin · Data Integrity</span>
            <h1 className="m-h1" style={{marginTop:2}}>Inventory <em>health</em></h1>
            <p className="m-muted" style={{maxWidth:640, marginTop:4}}>
              Single-pane audit of every location + photo field that powers the /spaces search filters. Auto-runs nightly; run manually below any time.
            </p>
          </div>
          <div style={{display:"flex", gap:10, alignItems:"center"}}>
            <button type="button" onClick={load} disabled={loading}
              style={{padding:"9px 16px", background:"transparent", color:"#1B2340", border:"1px solid rgba(27,35,64,0.35)", borderRadius:999, fontSize:13, cursor:"pointer"}}
              data-testid="inventory-health-refresh">
              {loading ? "Loading…" : "Refresh"}
            </button>
            <button type="button" onClick={sendDigest} disabled={sending}
              style={{padding:"9px 16px", background:"transparent", color:"#1B2340", border:"1px solid rgba(27,35,64,0.35)", borderRadius:999, fontSize:13, cursor: sending ? "not-allowed" : "pointer"}}
              data-testid="inventory-health-send-digest">
              {sending ? "Sending…" : "Send Digest Now"}
            </button>
            <button type="button" onClick={runSelfHeal} disabled={healing || loading}
              style={{padding:"9px 20px", background:"#B8913A", color:"#FAF8F3", border:"none", borderRadius:999, fontSize:13, fontWeight:500, cursor: (healing || loading) ? "not-allowed" : "pointer"}}
              data-testid="inventory-health-self-heal">
              {healing ? "Healing…" : "Run Self-Heal"}
            </button>
          </div>
        </div>

        {err && <div style={{background:"#FDEDED", border:"1px solid #E5A0A0", padding:"10px 14px", borderRadius:8, marginTop:16, fontSize:13}}>{err}</div>}
        {digestMsg && <div data-testid="digest-msg" style={{background:"#EFF6EB", border:"1px solid #C7E0B5", padding:"10px 14px", borderRadius:8, marginTop:16, fontSize:13}}>{digestMsg}</div>}

        {lastHealResult && (
          <div data-testid="inventory-heal-summary"
            style={{background:"#EFF6EB", border:"1px solid #C7E0B5", padding:"12px 16px", borderRadius:10, marginTop:16, fontSize:13}}>
            <div style={{fontFamily:"Cormorant Garamond, serif", fontSize:20, marginBottom:6}}>
              ✓ Self-heal complete
            </div>
            <div style={{color:"rgba(27,35,64,0.75)"}}>
              States patched: <b>{lastHealResult.steps.states_patched}</b> · Workspaces resynced: <b>{lastHealResult.steps.workspaces_resynced}</b> · Photos backfilled: <b>{lastHealResult.steps.photos_backfilled}</b>
            </div>
          </div>
        )}

        {report && (
          <>
            <div style={{marginTop:24, display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(220px,1fr))", gap:14}}>
              <StatCard label="Total venues" value={report.summary.totals.venues} testid="stat-venues" />
              <StatCard label="Total workspaces" value={report.summary.totals.workspaces} testid="stat-workspaces" />
              <StatCard label="Report generated" value={new Date(report.generated_at).toLocaleString()} testid="stat-generated" small />
            </div>

            <IssueBucket title="Venue issues" items={report.venue_issues}
              summary={report.summary.venue_issues} testid="venue" />
            <IssueBucket title="Workspace issues" items={report.workspace_issues}
              summary={report.summary.workspace_issues} testid="workspace" />
          </>
        )}
      </div>
    </AppShell>
  );
}

function StatCard({ label, value, testid, small }) {
  return (
    <div data-testid={testid}
      style={{background:"#FAF8F3", border:"1px solid rgba(184,145,58,0.28)", borderRadius:12, padding:"16px 18px"}}>
      <div style={{fontSize:11, letterSpacing:"0.14em", textTransform:"uppercase", color:"#B8913A", fontWeight:600}}>{label}</div>
      <div style={{fontFamily:"Cormorant Garamond, serif", fontSize: small ? 16 : 34, marginTop:6, color:"#1B2340", lineHeight:1.15}}>{value}</div>
    </div>
  );
}

function IssueBucket({ title, items, summary, testid }) {
  const keys = Object.keys(summary);
  const total = keys.reduce((n, k) => n + (summary[k] || 0), 0);
  return (
    <section style={{marginTop:28}} data-testid={`bucket-${testid}`}>
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:8}}>
        <h2 style={{fontFamily:"Cormorant Garamond, serif", fontSize:26, margin:0, fontWeight:500}}>{title}</h2>
        <span style={{fontSize:12, color: total > 0 ? "#B25A2A" : "rgba(27,35,64,0.5)", letterSpacing:"0.06em"}}>
          {total > 0 ? `${total} issues` : "all clear"}
        </span>
      </div>
      {keys.map(k => {
        const n = summary[k] || 0;
        const rows = items[k] || [];
        return (
          <details key={k} style={{background:"#FDF9F0", border:"1px solid rgba(184,145,58,0.22)", borderRadius:10, marginBottom:8, padding:"10px 14px"}}>
            <summary style={{cursor:"pointer", fontSize:13, display:"flex", justifyContent:"space-between", listStyle:"none"}}>
              <span style={{textTransform:"capitalize"}}>{k.replaceAll("_", " ")}</span>
              <span style={{fontFamily:"ui-monospace, monospace", color: n>0 ? "#B25A2A" : "rgba(27,35,64,0.4)"}}>{n}</span>
            </summary>
            {n > 0 && (
              <ul style={{margin:"10px 0 4px", padding:"0 0 0 18px", fontSize:12, color:"rgba(27,35,64,0.75)"}}>
                {rows.slice(0, 25).map((r, i) => (
                  <li key={i} style={{padding:"3px 0"}}>
                    <b>{r.name || r.slug || r.id}</b>{r.venue_id ? <span style={{color:"rgba(27,35,64,0.5)"}}> · venue={r.venue_id}</span> : null}
                    {r.status ? <span style={{color:"#B25A2A"}}> · {r.status}</span> : null}
                  </li>
                ))}
                {rows.length > 25 && <li style={{color:"rgba(27,35,64,0.5)"}}>… {rows.length - 25} more</li>}
              </ul>
            )}
          </details>
        );
      })}
    </section>
  );
}
